// Splits the server-composed activity message into bold/plain segments (A-32):
// the entry's subject/target codes, and status names in "status change(d)
// [from A] to B" / "with status A" sentences.

export type MessageSegment = { text: string; bold: boolean };

type ActivityLogEntry = {
	message?: string;
	subject?: { code?: string } | null;
	target?: { code?: string } | null;
};

const STATUS_CHANGE_PATTERN =
	/^(.*\bstatus changed? )(?:from (.+?) to |to )(.+)$/;
const WITH_STATUS_PATTERN = /^(.*\bwith status )(.+)$/;

function splitStatuses(message: string): MessageSegment[] {
	const withStatus = WITH_STATUS_PATTERN.exec(message);
	if (withStatus) {
		const [, lead, status] = withStatus;
		return [
			{ text: lead, bold: false },
			{ text: status, bold: true },
		];
	}

	const match = STATUS_CHANGE_PATTERN.exec(message);
	if (!match) return [{ text: message, bold: false }];

	const [, lead, fromStatus, toStatus] = match;
	return [
		{ text: lead, bold: false },
		...(fromStatus
			? [
					{ text: "from ", bold: false },
					{ text: fromStatus, bold: true },
					{ text: " to ", bold: false },
				]
			: [{ text: "to ", bold: false }]),
		{ text: toStatus, bold: true },
	];
}

function splitOn(text: string, code: string): MessageSegment[] {
	return text
		.split(code)
		.flatMap((part, index) =>
			index === 0
				? [{ text: part, bold: false }]
				: [
						{ text: code, bold: true },
						{ text: part, bold: false },
					]
		)
		.filter((segment) => segment.text);
}

export function activityLogSegments(entry: ActivityLogEntry): MessageSegment[] {
	const { message, subject, target } = entry;
	if (!message) return [];

	const codes = [subject?.code, target?.code].filter(Boolean);
	const segments = codes.reduce(
		(acc, code) =>
			acc.flatMap((segment) =>
				segment.bold ? [segment] : splitOn(segment.text, code)
			),
		splitStatuses(message)
	);
	return mergeAdjacent(segments);
}

function mergeAdjacent(segments: MessageSegment[]): MessageSegment[] {
	return segments.reduce((merged, segment) => {
		const last = merged[merged.length - 1];
		if (last?.bold === segment.bold) {
			last.text += segment.text;
		} else {
			merged.push({ ...segment });
		}
		return merged;
	}, [] as MessageSegment[]);
}
